// src/components/shooting/ShootingBlock.tsx
import { useEffect, useMemo, useState, useCallback } from "react";
import { PlusCircle, Trash2, Save, Target, Users, Pencil, X } from "lucide-react";
import { supabase } from "../supabaseClient";
import i18n from "../i18n";

// --- mini-hook i18n safe ---
function useI18nSafe() {
  const [lng, setLng] = useState(
    (i18n.resolvedLanguage || i18n.language || "fr").toLowerCase()
  );
  useEffect(() => {
    const onChange = (l: string) => setLng(l.toLowerCase());
    i18n.on("languageChanged", onChange);
    return () => i18n.off("languageChanged", onChange);
  }, []);
  const t = useCallback(
    (key: string, options?: Record<string, unknown>): string =>
      options != null
        ? String((i18n.t as any)(key, { ...options, lng }))
        : String((i18n.t as any)(key, { lng })),
    [lng]
  );
  return { t, lng };
}

type PlayerLite = { firstName: string; lastName: string; number: number };
type Training = { id: string };

type EntryRow = {
  id?: string;
  player_number: number;
  player_name: string;
  location: string;
  made: number;
  attempted: number;
  series_no?: number; // index de série
};

type ShootingBlockProps = {
  training: Training;
  players: PlayerLite[];
  defaultTitle?: string;
  onSaved?: () => void;
  /** Cache le bouton "Modifier" pour éviter l’auto-nesting quand rendu dans la modale */
  hideEditButton?: boolean;
};

// util: détecte le libellé par défaut (i18n) pour l’effacer si présent
const normalizeDefaultTitle = (raw: string | undefined, t: (k: string)=>string) => {
  const def = (t("shooting:defaultTitle") || "").trim().toLowerCase();
  const val = (raw || "").trim();
  return val.toLowerCase() === def ? "" : val;
};

export default function ShootingBlock({
  training,
  players,
  defaultTitle,
  onSaved,
  hideEditButton = false,
}: ShootingBlockProps) {
  const { t } = useI18nSafe();

  const SHOOT_LOCATIONS = useMemo(
    () => [
      t("shooting:positions.freeThrow"),
      t("shooting:positions.layupRight"),
      t("shooting:positions.layupLeft"),
      t("shooting:positions.mid0Left"),
      t("shooting:positions.mid0Right"),
      t("shooting:positions.midTop"),
      t("shooting:positions.midWingLeft"),
      t("shooting:positions.midWingRight"),
      t("shooting:positions.corner3Left"),
      t("shooting:positions.corner3Right"),
      t("shooting:positions.wing3Left"),
      t("shooting:positions.wing3Right"),
      t("shooting:positions.top3"),
    ],
    [t]
  );

  const [blockId, setBlockId] = useState<string | null>(null);

  // Historique (gelé) + série en cours (éditable)
  const [allEntries, setAllEntries] = useState<EntryRow[]>([]);
  const [rows, setRows] = useState<EntryRow[]>([]);

  const [title, setTitle] = useState<string>(normalizeDefaultTitle(defaultTitle, t));
  const [notes, setNotes] = useState("");
  const [selectedNumbers, setSelectedNumbers] = useState<number[]>([]);
  const [saving, setSaving] = useState(false);

  const [currentSeriesNo, setCurrentSeriesNo] = useState<number>(1);

  // --- état de la modale qui réutilise ShootingBlock
  const [showSelfModal, setShowSelfModal] = useState(false);

  const playersByNumber = useMemo(() => {
    const map = new Map<number, PlayerLite>();
    players?.forEach((p) => map.set(p.number, p));
    return map;
  }, [players]);

  // Charger (ou créer) le bloc + récupérer l’historique
  useEffect(() => {
    (async () => {
      if (!training?.id) return;
      const { data: blocks, error } = await supabase
        .from("training_shoot_blocks")
        .select("*")
        .eq("training_id", training.id)
        .order("created_at", { ascending: true });

      if (error) {
        console.error(error);
        return;
      }

      if (blocks && blocks.length > 0) {
        const b = blocks[0];
        setBlockId(b.id);

        const incomingTitle = normalizeDefaultTitle(b.title ?? defaultTitle, t);
        setTitle(incomingTitle);
        setNotes(b.notes ?? "");

        const { data: entries, error: e2 } = await supabase
          .from("training_shoot_entries")
          .select("*")
          .eq("block_id", b.id)
          .order("created_at", { ascending: true });

        if (e2) {
          console.error(e2);
          return;
        }

        const clean = (entries ?? []).map((r: any) => ({
          id: r.id,
          player_number: r.player_number,
          player_name: r.player_name,
          location: r.location,
          made: r.made,
          attempted: r.attempted,
          series_no: r.series_no ?? 1,
        })) as EntryRow[];

        setAllEntries(clean);

        const maxSeries =
          clean.length > 0 ? Math.max(...clean.map((e) => e.series_no || 1)) : 0;
        setCurrentSeriesNo(maxSeries + 1);
      } else {
        const { data: newBlock, error: e3 } = await supabase
          .from("training_shoot_blocks")
          .insert({
            training_id: training.id,
            title: "",
            notes: "",
          })
          .select()
          .single();

        if (e3) {
          console.error(e3);
          return;
        }
        setBlockId(newBlock.id);
        setTitle("");
        setNotes("");
        setAllEntries([]);
        setCurrentSeriesNo(1);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [training?.id]);

  // Helpers
  const addRowForPlayer = (num: number) => {
    const p = playersByNumber.get(num);
    if (!p) return;
    setRows((prev) => [
      ...prev,
      {
        player_number: p.number,
        player_name: `${p.firstName} ${p.lastName}`,
        location: SHOOT_LOCATIONS[0],
        made: 0,
        attempted: 0,
        series_no: currentSeriesNo,
      },
    ]);
  };

  const removeRow = (idx: number) => {
    setRows((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSelectPlayers = (nums: number[]) => {
    setSelectedNumbers(nums);
    nums.forEach((n) => {
      const already = rows.some((r) => r.player_number === n);
      if (!already) addRowForPlayer(n);
    });
    setRows((prev) => prev.filter((r) => nums.includes(r.player_number)));
  };

  const handleUpdateRow = (idx: number, patch: Partial<EntryRow>) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  // Totaux = historique + série en cours (preview live)
  const totalsByPlayer = useMemo(() => {
    const m = new Map<number, { name: string; made: number; attempted: number }>();
    const accumulate = (arr: EntryRow[]) => {
      arr.forEach((r) => {
        const cur = m.get(r.player_number) ?? { name: r.player_name, made: 0, attempted: 0 };
        cur.made += Number(r.made || 0);
        cur.attempted += Number(r.attempted || 0);
        m.set(r.player_number, cur);
      });
    };
    accumulate(allEntries);
    accumulate(rows);
    return m;
  }, [allEntries, rows]);

  // Sauvegarde = gèle la série courante, puis réinitialise à 0 pour une nouvelle série
  const saveAll = async () => {
    if (!blockId) return;
    setSaving(true);
    try {
      await supabase
        .from("training_shoot_blocks")
        .update({
          title: normalizeDefaultTitle(title, t),
          notes,
        })
        .eq("id", blockId);

      const payload = rows.map((r) => ({
        block_id: blockId,
        player_number: r.player_number,
        player_name: r.player_name,
        location: r.location,
        made: Math.max(0, Number(r.made || 0)),
        attempted: Math.max(0, Number(r.attempted || 0)),
        series_no: currentSeriesNo,
      }));

      if (payload.length > 0) {
        const { error: insErr } = await supabase.from("training_shoot_entries").insert(payload);
        if (insErr) throw insErr;

        setAllEntries((prev) => [...prev, ...(payload as any)]);
      }

      const nextSeries = currentSeriesNo + 1;
      setCurrentSeriesNo(nextSeries);
      setRows(
        selectedNumbers.map((num) => {
          const p = playersByNumber.get(num)!;
          return {
            player_number: p.number,
            player_name: `${p.firstName} ${p.lastName}`,
            location: SHOOT_LOCATIONS[0],
            made: 0,
            attempted: 0,
            series_no: nextSeries,
          } as EntryRow;
        })
      );

      onSaved?.();
    } catch (e) {
      console.error(e);
      alert("Error saving shooting block");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 shadow-sm">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Target className="w-5 h-5" />
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="flex-1 rounded-md border border-gray-300 dark:border-gray-700 bg-transparent px-3 py-2 text-sm"
          placeholder={t("shooting:defaultTitle")}
          autoComplete="off"
        />
        <span className="text-xs px-2 py-1 rounded bg-gray-100 dark:bg-gray-800">
          {(t("shooting:series") || "Série")} #{currentSeriesNo}
        </span>

        {/* Bouton pour ouvrir la modale d'édition basée sur ShootingBlock lui-même */}
        {!hideEditButton && (
          <button
            type="button"
            onClick={() => setShowSelfModal(true)}
            className="ml-auto inline-flex items-center gap-2 px-3 py-2 rounded-md border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
            title={t("shooting:editHistory") || "Modifier le bloc de tirs"}
          >
            <Pencil className="w-4 h-4" />
            {t("shooting:editHistory") || "Modifier"}
          </button>
        )}
      </div>

      <div className="mb-3">
        <label className="flex items-center gap-2 text-sm font-medium mb-2">
          <Users className="w-4 h-4" /> {t("shooting:playersSelect")}
        </label>
        <div className="flex flex-wrap gap-2">
          {players.map((p) => {
            const active = selectedNumbers.includes(p.number);
            return (
              <button
                key={p.number}
                type="button"
                onClick={() =>
                  active
                    ? handleSelectPlayers(selectedNumbers.filter((n) => n !== p.number))
                    : handleSelectPlayers([...selectedNumbers, p.number])
                }
                className={`px-3 py-1 rounded-full border text-sm ${
                  active
                    ? "bg-blue-600 text-white border-blue-600"
                    : "border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
                }`}
              >
                #{p.number} {p.firstName}
              </button>
            );
          })}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left border-b border-gray-200 dark:border-gray-800">
              <th className="py-2 pr-2">{t("shooting:columns.player")}</th>
              <th className="py-2 px-2">{t("shooting:columns.zone")}</th>
              <th className="py-2 px-2">{t("shooting:columns.made")}</th>
              <th className="py-2 px-2">{t("shooting:columns.attempted")}</th>
              <th className="py-2 px-2">{t("shooting:columns.pct")}</th>
              <th className="py-2 pl-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => {
              const pct = r.attempted > 0 ? Math.round((r.made / r.attempted) * 100) : 0;
              return (
                <tr key={idx} className="border-b border-gray-100 dark:border-gray-800">
                  <td className="py-2 pr-2 whitespace-nowrap">
                    {r.player_name}{" "}
                    <span className="text-xs text-gray-500">#{r.player_number}</span>
                  </td>
                  <td className="py-2 px-2">
                    <select
                      value={r.location}
                      onChange={(e) => handleUpdateRow(idx, { location: e.target.value })}
                      className="rounded-md border border-gray-300 dark:border-gray-700 bg-transparent px-2 py-1"
                    >
                      {SHOOT_LOCATIONS.map((loc) => (
                        <option key={loc} value={loc}>
                          {loc}
                        </option>
                      ))}
                      <option value="Other">Other</option>
                    </select>
                  </td>
                  <td className="py-2 px-2">
                    <input
                      type="number"
                      min={0}
                      value={r.made}
                      onChange={(e) => {
                        const made = Math.max(0, Number(e.target.value || 0));
                        handleUpdateRow(idx, {
                          made,
                          attempted: Math.max(made, r.attempted),
                        });
                      }}
                      className="w-20 rounded-md border border-gray-300 dark:border-gray-700 bg-transparent px-2 py-1"
                    />
                  </td>
                  <td className="py-2 px-2">
                    <input
                      type="number"
                      min={0}
                      value={r.attempted}
                      onChange={(e) => {
                        const attempted = Math.max(0, Number(e.target.value || 0));
                        handleUpdateRow(idx, {
                          attempted: Math.max(attempted, r.made),
                        });
                      }}
                      className="w-20 rounded-md border border-gray-300 dark:border-gray-700 bg-transparent px-2 py-1"
                    />
                  </td>
                  <td className="py-2 px-2">{pct}%</td>
                  <td className="py-2 pl-2">
                    <button
                      type="button"
                      onClick={() => removeRow(idx)}
                      className="inline-flex items-center gap-1 text-red-600 hover:text-red-700"
                      title="Delete row"
                    >
                      <Trash2 className="w-4 h-4" /> X
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {totalsByPlayer.size > 0 && (
        <div className="mt-4 overflow-x-auto">
          <table className="min-w-[420px] text-sm">
            <thead>
              <tr className="text-left border-b border-gray-200 dark:border-gray-800">
                <th className="py-2 pr-2">{t("shooting:columns.player")}</th>
                <th className="py-2 px-2">{t("shooting:columns.total")}</th>
                <th className="py-2 px-2">{t("shooting:columns.pct")}</th>
              </tr>
            </thead>
            <tbody>
              {[...totalsByPlayer.entries()].map(([num, agg]) => {
                const pct =
                  agg.attempted > 0 ? Math.round((agg.made / agg.attempted) * 100) : 0;
                return (
                  <tr key={num} className="border-b border-gray-100 dark:border-gray-800">
                    <td className="py-2 pr-2">
                      {agg.name} <span className="text-xs text-gray-500">#{num}</span>
                    </td>
                    <td className="py-2 px-2">
                      {agg.made} / {agg.attempted}
                    </td>
                    <td className="py-2 px-2">{pct}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-4 flex items-center gap-2">
        <button
          type="button"
          onClick={() => {
            // ajoute une nouvelle série (ligne vide) pour chaque joueuse sélectionnée
            selectedNumbers.forEach((n) => addRowForPlayer(n));
          }}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
        >
          <PlusCircle className="w-4 h-4" /> {t("shooting:addSeries")}
        </button>

        <button
          type="button"
          onClick={saveAll}
          disabled={saving || rows.length === 0}
          className="ml-auto inline-flex items-center gap-2 px-3 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white"
        >
          <Save className="w-4 h-4" />
          {saving ? t("shooting:saving") : t("shooting:save")}
        </button>
      </div>

      <div className="mt-3">
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          placeholder={t("shooting:notesPlaceholder")}
          className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-transparent px-3 py-2 text-sm"
        />
      </div>

      {/* ====== MODALE qui RÉUTILISE ce composant ====== */}
      {showSelfModal && (
        <div className="fixed inset-0 z-50">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowSelfModal(false)}
            aria-hidden
          />
          {/* Dialog */}
          <div
            className="relative z-10 mx-auto mt-[5vh] w-[95vw] max-w-5xl max-h-[88vh] overflow-hidden rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 shadow-xl"
            role="dialog"
            aria-modal="true"
          >
            <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200 dark:border-gray-800">
              <Pencil className="w-4 h-4" />
              <h3 className="font-semibold text-sm">{t("shooting:editHistory") || "Modifier le bloc de tirs"}</h3>
              <button
                onClick={() => setShowSelfModal(false)}
                className="ml-auto p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800"
                aria-label={t("common:close") || "Fermer"}
                title={t("common:close") || "Fermer"}
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-4 overflow-auto" style={{ maxHeight: "78vh" }}>
              {/* 🔁 Réutilise le même composant dans la modale */}
              <ShootingBlock
                training={training}
                players={players}
                defaultTitle={defaultTitle}
                onSaved={() => {
                  // Option: refermer à chaque save
                  // setShowSelfModal(false);
                  onSaved?.();
                }}
                hideEditButton // 👈 IMPORTANT: évite que le bouton réouvre une modale dans la modale
              />
            </div>
          </div>
        </div>
      )}
      {/* ====== FIN MODALE ====== */}
    </div>
  );
}
